import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, X } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { PUBLICATIONS, PUBLICATION_TYPES, PUBLICATION_DOMAINS } from "@/data/publications";
import { useLang } from "@/i18n/LanguageContext";

export default function Publications() {
  const { t, lang } = useLang();
  const PUB = t.publications;

  const [year, setYear] = useState("all");
  const [type, setType] = useState("all");
  const [domain, setDomain] = useState("all");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const years = useMemo(
    () => Array.from(new Set(PUBLICATIONS.map((p) => p.year))).sort((a, b) => b - a),
    []
  );

  const filtered = useMemo(
    () =>
      PUBLICATIONS.filter(
        (p) =>
          (year === "all" || p.year === Number(year)) &&
          (type === "all" || p.type === type) &&
          (domain === "all" || p.domain === domain)
      ).sort((a, b) => b.year - a.year || a.id.localeCompare(b.id)),
    [year, type, domain]
  );

  const clearAll = () => {
    setYear("all");
    setType("all");
    setDomain("all");
  };

  const anyFilter = year !== "all" || type !== "all" || domain !== "all";

  return (
    <main data-testid="publications-page" className="relative bg-[#101012] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-16 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 noise" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="publications-back-home"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white transition-colors mb-12"
          >
            {PUB.back_home}
          </Link>

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="label-mono mb-4">{PUB.page_label}</div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="display-1 text-white text-4xl md:text-6xl lg:text-7xl"
              >
                {PUB.title_a}
                <br />
                <span className="text-white/40">{PUB.title_muted}</span>{" "}
                <span className="text-[#FF3333]">{PUB.title_accent}</span>
              </motion.h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-white/70 text-base leading-relaxed">{PUB.intro}</p>
              <div className="mt-6 font-mono text-[10px] tracking-widest uppercase text-white/40">
                <span data-testid="publications-count" className="text-white font-display text-3xl font-bold mr-2">
                  {filtered.length}
                </span>
                / {PUBLICATIONS.length} {PUB.total}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-white/10 bg-[#0a0a0c] sticky top-16 z-30 backdrop-blur-xl">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-4">
          <FilterGroup
            testid="filter-year"
            label={PUB.filter_year}
            value={year}
            onChange={setYear}
            options={[{ v: "all", l: PUB.filter_all }, ...years.map((y) => ({ v: String(y), l: String(y) }))]}
          />
          <FilterGroup
            testid="filter-type"
            label={PUB.filter_type}
            value={type}
            onChange={setType}
            options={[{ v: "all", l: PUB.filter_all }, ...PUBLICATION_TYPES.map((tp) => ({ v: tp, l: tp }))]}
          />
          <FilterGroup
            testid="filter-domain"
            label={PUB.filter_domain}
            value={domain}
            onChange={setDomain}
            options={[{ v: "all", l: PUB.filter_all }, ...PUBLICATION_DOMAINS.map((d) => ({ v: d, l: d }))]}
          />
          {anyFilter && (
            <button
              type="button"
              data-testid="filter-clear"
              onClick={clearAll}
              className="ml-auto inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 transition-colors"
            >
              <X size={12} /> {PUB.filter_clear}
            </button>
          )}
        </div>
      </section>

      {/* List */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          {filtered.length === 0 ? (
            <div
              data-testid="publications-empty"
              className="border border-white/10 p-16 text-center text-white/50 font-mono text-sm tracking-wider"
            >
              {PUB.no_results}
            </div>
          ) : (
            <div className="border border-white/10">
              {filtered.map((p, i) => {
                const title = lang === "ko" ? p.title_ko : p.title;
                const citation = lang === "ko" ? p.citation_ko : p.citation;
                return (
                  <motion.article
                    key={p.id}
                    data-testid={`publication-${p.id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                    className={`group grid grid-cols-12 gap-6 items-start p-8 lg:p-10 hover:bg-[#161618] transition-colors ${
                      i < filtered.length - 1 ? "border-b border-white/10" : ""
                    }`}
                  >
                    <div className="col-span-12 lg:col-span-2">
                      <div className="font-display font-bold text-3xl text-white">{p.year}</div>
                      <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-white/40">
                        {p.id}
                      </div>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] border border-[#FF3333]/40 px-2 py-1">
                          {p.type}
                        </span>
                        <span className="font-mono text-[10px] tracking-widest uppercase text-white/50 border border-white/15 px-2 py-1">
                          {p.domain}
                        </span>
                        <span className="font-mono text-[10px] tracking-widest uppercase text-white/50 border border-white/15 px-2 py-1">
                          {p.venue}
                        </span>
                      </div>
                      <h3 className="font-display font-bold text-xl md:text-2xl text-white tracking-tight">
                        {title}
                      </h3>
                      <p className="mt-3 text-white/55 text-sm leading-relaxed">{citation}</p>
                    </div>

                    <div className="col-span-12 lg:col-span-3 flex lg:justify-end">
                      <a
                        href={p.url}
                        data-testid={`publication-link-${p.id}`}
                        className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-white/70 hover:text-[#FF3333] border border-white/15 hover:border-[#FF3333] px-4 py-3 transition-colors"
                      >
                        {PUB.read} <ExternalLink size={12} />
                      </a>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function FilterGroup({ testid, label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-2">
      <span className="label-mono">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              data-testid={`${testid}-${o.v}`}
              onClick={() => onChange(o.v)}
              className={`font-mono text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors ${
                active
                  ? "bg-[#FF3333] border-[#FF3333] text-white"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}
